import { preloadImage } from '@theme/utilities';

/**
 * Placeholder image web component.
 *
 * By default, Shopify's native placeholder SVGs are rendered server-side inside this element.
 * When custom placeholder images are configured in Theme.placeholders, this component
 * replaces the default content with the custom images.
 *
 * To add custom placeholders for a client, add image URLs to Theme.placeholders in scripts.liquid.
 */

/** @type {Record<string, number>} */
const cache = {};

/**
 * Check if custom placeholders are configured for a given type
 * @param {'product' | 'general'} type
 * @returns {boolean}
 */
const hasCustomPlaceholders = (type) => {
  return Theme.placeholders?.[type]?.length > 0;
};

// Initialize counters only if custom placeholders exist
const shopName = window.Shopify.shop;
const shopPlaceholderSeed = shopName.split('-').reduce((acc, char) => acc + char.charCodeAt(0), 0);
const counters = {
  product: 0,
  general: hasCustomPlaceholders('general') ? shopPlaceholderSeed % Theme.placeholders.general.length : 0,
};

// Preload custom placeholder images in preview/design mode
if (window.Shopify.visualPreviewMode || window.Shopify.designMode) {
  const placeholderUrls = Object.values(Theme.placeholders).flat();

  for (const url of placeholderUrls) {
    if (url) preloadImage(url);
  }
}

class PlaceholderImage extends HTMLElement {
  connectedCallback() {
    const type = /** @type {'product' | 'general'} */ (this.dataset.type);
    const blockId = this.dataset.blockId;

    // If no custom placeholders are configured, keep the default Shopify placeholder (server-rendered)
    if (!type || !hasCustomPlaceholders(type)) return;

    if (!blockId) return;

    // Check cache for this block
    const cached = cache[blockId];
    if (cached != null && Theme.placeholders[type][cached]) {
      this.#renderImage(Theme.placeholders[type][cached]);
      return;
    }

    // Get next placeholder in rotation
    const counter = counters[type] || 0;
    counters[type] = (counter + 1) % Theme.placeholders[type].length;

    const image = Theme.placeholders[type][counter];
    if (!image) return;

    this.#renderImage(image);
    cache[blockId] = counter;
  }

  /**
   * Clear default content and render custom placeholder image
   * @param {string} src
   */
  #renderImage(src) {
    this.innerHTML = '';
    const imageElement = document.createElement('img');
    imageElement.src = src;
    imageElement.alt = Theme.translations.placeholder_image || 'Placeholder Image';
    this.appendChild(imageElement);
  }
}

if (!customElements.get('placeholder-image')) {
  customElements.define('placeholder-image', PlaceholderImage);
}
